import { DESPESAS_OPERACIONAIS, type ChaveDeMeta, type Dre, type LinhaCalculada } from "@bookdental/schemas";

export type Metas = Partial<Record<ChaveDeMeta, number>>;

/** As metas (valores positivos) no formato de um DRE, com os subtotais: a coluna Meta da tabela. */
export function dreDasMetas(m: Metas): Dre {
  const v = (k: ChaveDeMeta) => m[k] ?? 0;
  const linha = (valorCents: number): LinhaCalculada => ({ valorCents, categorias: [] });
  const despesas = Object.fromEntries(DESPESAS_OPERACIONAIS.map((d) => [d, linha(d in m ? v(d as ChaveDeMeta) : 0)])) as Dre["despesas"];
  const receitaBrutaCents = v("faturamento") + v("outrasReceitas");
  const receitaLiquidaCents = receitaBrutaCents - v("descontos") - v("deducoes");
  const lucroBrutoCents = receitaLiquidaCents - v("custo");
  const despesasOperacionaisCents = Object.values(despesas).reduce((t, d) => t + d.valorCents, 0);
  const resultadoOperacionalCents = lucroBrutoCents - despesasOperacionaisCents;
  return {
    faturamentoCents: v("faturamento"),
    outrasReceitas: linha(v("outrasReceitas")),
    receitaBrutaCents,
    descontos: linha(v("descontos")),
    deducoes: { ...linha(v("deducoes")), estimado: false },
    receitaLiquidaCents,
    custoDosServicos: linha(v("custo")),
    lucroBrutoCents,
    despesas,
    despesasOperacionaisCents,
    resultadoOperacionalCents,
    depreciacao: linha(v("depreciacao")),
    financeiro: { entradas: linha(0), saidas: linha(v("financeiro")), valorCents: -v("financeiro") },
    impostoSobreOLucro: { ...linha(v("ir")), estimado: false, estimadoCents: 0 },
    provisaoCents: 0,
    resultadoCents: resultadoOperacionalCents - v("depreciacao") - v("financeiro") - v("ir"),
    foraDoResultado: { entradas: linha(0), saidas: linha(0) },
    margem: { custoDaProducaoCents: 0, outrosVariaveisCents: 0, fixosCents: 0 },
  };
}

/** O realizado de um DRE em valores positivos, por linha com meta (para copiar como meta). */
export function metasDoRealizado(d: Dre, ajustePct = 0): Metas {
  const f = (c: number) => Math.max(0, Math.round(c * (1 + ajustePct / 100)));
  return {
    faturamento: f(d.faturamentoCents),
    outrasReceitas: f(d.outrasReceitas.valorCents),
    descontos: f(d.descontos.valorCents),
    deducoes: f(d.deducoes.valorCents),
    custo: f(d.custoDosServicos.valorCents),
    PESSOAL: f(d.despesas.PESSOAL.valorCents),
    OCUPACAO: f(d.despesas.OCUPACAO.valorCents),
    ADMINISTRATIVA: f(d.despesas.ADMINISTRATIVA.valorCents),
    COMERCIAL: f(d.despesas.COMERCIAL.valorCents),
    depreciacao: f(d.depreciacao.valorCents),
    financeiro: f(-d.financeiro.valorCents),
    ir: f(d.impostoSobreOLucro.valorCents),
  };
}
